import React from "react";
import {
  ArrowWrapper,
  ButtonArrow,
  CarouselWrapper,
  Container,
  Dots,
  IndicatorWrapper,
} from "./CardSlider.styled";
// @ts-ignore
import { ReactComponent as ChevronLeft } from "../../../assets/img/illustrations/ArrowLeft.svg";
// @ts-ignore
import { ReactComponent as ChevronRight } from "../../../assets/img/illustrations/ArrowRight.svg";
import { useScrollIntoView } from "../Tabs/utils";

export interface CardSliderProps {
  isDarkMode?: boolean;
  totalData: number;
}

export interface CardAttributeProps {
  id: number;
  width: number;
  xPosition: number;
}

const CardSlider: React.FC<React.PropsWithChildren<CardSliderProps>> = ({
  children,
  isDarkMode = false,
  totalData = 0,
}) => {
  const carouselWrapper = React.useRef<HTMLDivElement | null>(null);

  const { scrollTo } = useScrollIntoView();

  const [parentWidth, setParentWidth] = React.useState<number>(0);
  const [totalCard, setTotalCard] = React.useState<number>(0);
  const [currentIndex, setCurrentIndex] = React.useState<number>(0);
  const [cardAttributeList, setCardAttributeList] = React.useState<CardAttributeProps[]>([]);

  const indicatorConfig = React.useMemo(() => {
    const cardWidth = cardAttributeList?.[0]?.width || 0;

    if (cardWidth > 0 && parentWidth > 0) {
      /* Ukur dari lebar card */
      const _totalShowCard = Math.floor(parentWidth / cardWidth);
      const totalIndicator = Math.abs(totalCard - _totalShowCard) + 1;

      return {
        list: Array(totalIndicator)
          ?.fill(null)
          ?.map((_, idx) => idx),
        isShowIndicator: totalCard > _totalShowCard,
      };
    }

    return {
      list: [],
      isShowIndicator: false,
    };
  }, [totalCard, cardAttributeList, parentWidth]);

  const handleClickNext = () => {
    if (carouselWrapper?.current) {
      const scrollWidth = carouselWrapper?.current?.scrollWidth || 0;

      const _currentIndex = currentIndex + 1;
      const _leftPosition = cardAttributeList?.[_currentIndex]?.xPosition;

      const isLast = _currentIndex >= indicatorConfig?.list?.length - 1;
      const xPosition = isLast ? scrollWidth : _leftPosition;

      scrollTo({
        top: 0,
        left: xPosition,
        horizontal: true,
        averageWidth: cardAttributeList?.[0]?.width,
        scrollableContainer: carouselWrapper?.current,
        /** ketika mendapatkan error ataupun ternyata tab bukan scrolling element maka bisa menggunakan callback ini */
        errorCallback: () => {
          console.log("ERROR CALLBACK");
        },
      })?.then((_) => {
        setCurrentIndex(_currentIndex);
      });
    }
  };

  const handleClickBack = () => {
    if (carouselWrapper?.current) {
      const _currentIndex = currentIndex - 1;
      const _leftPosition = cardAttributeList?.[_currentIndex]?.xPosition;

      scrollTo({
        top: 0,
        left: _leftPosition,
        horizontal: true,
        averageWidth: cardAttributeList?.[0]?.width,
        scrollableContainer: carouselWrapper?.current,
        /** ketika mendapatkan error ataupun ternyata tab bukan scrolling element maka bisa menggunakan callback ini */
        errorCallback: () => {
          console.log("ERROR CALLBACK");
        },
      })?.then((_) => {
        setCurrentIndex(_currentIndex);
      });
    }
  };

  React.useEffect(() => {
    if (carouselWrapper?.current) {
      const _parentWidth = carouselWrapper?.current?.clientWidth || 0;
      const _clientChildren = carouselWrapper?.current?.children;

      setParentWidth(_parentWidth);

      if (typeof _clientChildren === "object" && _clientChildren?.length > 0) {
        const _totalCard = _clientChildren?.length || 0;
        // collect total card
        setTotalCard(_totalCard);

        // collect card attribute props
        let accumulativeWidth = 0;
        const cardAttributeArray: CardAttributeProps[] = [];

        Array(_totalCard)
          ?.fill(null)
          ?.forEach((_, index: number) => {
            const cardWidth = _clientChildren?.[index]?.clientWidth;

            cardAttributeArray.push({
              id: index,
              width: cardWidth,
              xPosition: accumulativeWidth,
            });

            accumulativeWidth += cardWidth;
          });

        setCardAttributeList(cardAttributeArray);
      }
    }
  }, [carouselWrapper?.current, totalData]);

  return (
    <React.Fragment>
      <Container>
        <ArrowWrapper>
          {indicatorConfig?.isShowIndicator && (
            <ButtonArrow
              onClick={currentIndex <= 0 ? undefined : handleClickBack}
              isDarkMode={isDarkMode}
              isDisabled={currentIndex <= 0}
              opacity="1"
            >
              <ChevronLeft />
            </ButtonArrow>
          )}
        </ArrowWrapper>

        <CarouselWrapper isShowIndicator={indicatorConfig?.isShowIndicator} ref={carouselWrapper}>
          {children}
        </CarouselWrapper>

        <ArrowWrapper>
          {indicatorConfig?.isShowIndicator && (
            <ButtonArrow
              onClick={
                currentIndex >= indicatorConfig?.list?.length - 1 ? undefined : handleClickNext
              }
              isDarkMode={isDarkMode}
              isDisabled={currentIndex >= indicatorConfig?.list?.length - 1}
              opacity="1"
            >
              <ChevronRight />
            </ButtonArrow>
          )}
        </ArrowWrapper>
      </Container>
      {indicatorConfig?.isShowIndicator && (
        <IndicatorWrapper>
          {indicatorConfig?.list?.map((index) => (
            <Dots isActive={currentIndex === index} activeDotColor="grey" />
          ))}
        </IndicatorWrapper>
      )}
    </React.Fragment>
  );
};

export default CardSlider;
